/** @format */

import React, { FC, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Card,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Icon,
  Timepicker,
  Datepicker,
  Button,
  dayjs,
  Skeleton,
  Message
} from '@king-design/react'
import { useHistory, useLocation } from 'react-router'
import { ScheduleItemProps } from './interface'
import { initScheduleValues } from './variables'
import { AddButon, CustomPageContainer } from 'src/components'
import { cloneDeep } from 'lodash'
import { useAsync } from 'react-use'
import API from 'src/api/ApiContacts'
import variable from 'src/static/styles/var'
import { request } from 'src/utils'
import PreviewTable from './components/PreviewTable'
import { getScheduleDetail, createSchedule, updateSchedule } from 'src/api/ApiContacts'

interface EditPageProps {
  className?: string
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const EditPage: FC<EditPageProps> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const id = query.get('id')
  // form表单
  const formRef = useRef(null)
  const [formValue, setFormValue] = useState<ScheduleItemProps>(initScheduleValues)
  const pageTitle = useMemo(() => {
    return id ? _$('编辑值班表') : _$('新建值班表')
  }, [id])

  // 获取值班表详情
  const { loading: detailLoading } = useAsync(async () => {
    if (!id) return null
    const result = await getScheduleDetail(id)
    if (result) {
      const { Name, Description, DutyShift, Period, ChangeTime, StartTime, EndTime } = result
      setFormValue({
        Name,
        Description,
        DutyShift,
        Period,
        ChangeTime,
        StartTime,
        EndTime
      })
    }
  }, [id])

  // 获取联系人列表
  const { value: contactList, loading: contactLoading } = useAsync(async () => {
    const result = await request(API.getAlertUser, {
      method: 'get',
      data: {
        PageIndex: 1,
        Cap: 1
      }
    })
    const { data } = result
    if (data.userlist?.length) {
      return data.userlist.filter(item => item.status === 1)
    }
    return []
  }, [])

  // 提交表单
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    try {
      if (validate) {
        const result = id
          ? await updateSchedule({
              ScheduleID: parseInt(id),
              ...formValue
            })
          : await createSchedule(formValue)
        if (result?.code === 'Success') {
          Message.success(_$('提交成功'))
          history.push(`/monitor/alarmServer/alertTarget?tab=schedule`)
        }
        return
      }
    } catch (error) {
      console.log('error', error)
    }
  }
  return (
    <div className={className}>
      <CustomPageContainer showBack title={pageTitle}>
        <Card type="none">
          {contactLoading ? (
            <Skeleton loading={contactLoading || detailLoading} />
          ) : (
            <>
              <Form starOnRequired ref={formRef} labelWidth={100}>
                <h1 className="edit_title">{_$('基础信息')}</h1>
                <FormItem
                  label={_$('值班表名')}
                  value={formValue.Name}
                  rules={{ required: true, userName: true }}
                >
                  <Input
                    value={formValue.Name}
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        Name: value
                      })
                    }}
                    placeholder={_$('请输入值班表名')}
                  ></Input>
                </FormItem>
                <FormItem label={_$('描述')} value={formValue.Description}>
                  <Input
                    type="textarea"
                    value={formValue.Description}
                    rows="5"
                    resize="none"
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        Description: value
                      })
                    }}
                    placeholder={_$('请输入描述')}
                  ></Input>
                </FormItem>
                <h1 className="edit_title">{_$('值班配置')}</h1>
                <FormItem
                  label={_$('值班班次')}
                  value={formValue.DutyShift}
                  rules={{ required: true }}
                >
                  {formValue.DutyShift?.map((item, index) => {
                    return (
                      <div key={index} className="duty_shift_item">
                        <i>#{index + 1}.</i>
                        <FormItem hideLabel value={item} rules={{ required: true }}>
                          <Select
                            width={500}
                            value={item.map(item => item.ContactID || '')}
                            multiple
                            filterable
                            clearable
                            onChangeValue={value => {
                              const cloneDutyShift = cloneDeep(formValue.DutyShift)
                              cloneDutyShift[index] = value.map(userid => {
                                const find = contactList.find(item => item.userid === userid)
                                return {
                                  ContactID: userid,
                                  ContactName: find?.name
                                }
                              })
                              setFormValue({
                                ...formValue,
                                DutyShift: cloneDutyShift
                              })
                            }}
                          >
                            {contactList?.map(item => {
                              return (
                                <Option key={item.userid} value={item.userid}>
                                  {item.name}
                                </Option>
                              )
                            })}
                          </Select>
                        </FormItem>
                        {index !== 0 ? (
                          <Icon
                            className="k-icon-up"
                            size={16}
                            onClick={() => {
                              const cloneDutyShift = cloneDeep(formValue.DutyShift)
                              const currentItem = cloneDutyShift[index]
                              cloneDutyShift[index] = cloneDutyShift[index - 1]
                              cloneDutyShift[index - 1] = currentItem
                              setFormValue({
                                ...formValue,
                                DutyShift: cloneDutyShift
                              })
                            }}
                          />
                        ) : null}
                        {index !== formValue.DutyShift?.length - 1 ? (
                          <Icon
                            className="k-icon-down"
                            size={16}
                            onClick={() => {
                              const cloneDutyShift = cloneDeep(formValue.DutyShift)
                              const currentItem = cloneDutyShift[index]
                              cloneDutyShift[index] = cloneDutyShift[index + 1]
                              cloneDutyShift[index + 1] = currentItem
                              setFormValue({
                                ...formValue,
                                DutyShift: cloneDutyShift
                              })
                            }}
                          />
                        ) : null}
                        {formValue.DutyShift?.length > 1 ? (
                          <Icon
                            className="ion-trash-a"
                            size={16}
                            onClick={() => {
                              const cloneDutyShift = cloneDeep(formValue.DutyShift)
                              cloneDutyShift.splice(index, 1)
                              setFormValue({
                                ...formValue,
                                DutyShift: cloneDutyShift
                              })
                            }}
                          />
                        ) : null}
                      </div>
                    )
                  })}

                  <AddButon
                    disabled={formValue.DutyShift?.length >= 10}
                    onClick={() => {
                      setFormValue({
                        ...formValue,
                        DutyShift: formValue.DutyShift.concat([[]])
                      })
                    }}
                  />
                </FormItem>
                <FormItem
                  label={_$('值班周期')}
                  value={formValue.Period}
                  rules={{ required: true }}
                >
                  <Select
                    value={formValue.Period}
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        Period: value
                      })
                    }}
                  >
                    <Option value="day">{_$('按天')}</Option>
                    <Option value="week">{_$('按周')}</Option>
                  </Select>
                </FormItem>
                <FormItem
                  label={_$('换班时间')}
                  value={formValue.ChangeTime}
                  rules={{ required: true }}
                >
                  <Timepicker
                    value={formValue.ChangeTime}
                    format="HH:mm"
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        ChangeTime: value
                      })
                    }}
                  />
                </FormItem>
                <FormItem
                  label={_$('开始时间')}
                  value={formValue.StartTime}
                  rules={{ required: true }}
                >
                  <Datepicker
                    type="datetime"
                    value={formValue.StartTime}
                    format="YYYY-MM-DD HH:mm"
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        StartTime: value
                      })
                    }}
                  />
                </FormItem>
                <FormItem label={_$('结束时间')} value={formValue.EndTime}>
                  <Datepicker
                    type="datetime"
                    clearable
                    value={formValue.EndTime}
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={date => {
                      return date.isBefore(dayjs(formValue.StartTime))
                    }}
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        EndTime: value
                      })
                    }}
                    placeholder={_$('请选择结束时间')}
                  />
                </FormItem>
              </Form>
              <h1 className="edit_title">{_$('预览')}</h1>
              <PreviewTable formValue={formValue} />
              <div className="bottom_action">
                <Button onClick={() => formSubmit()} type="primary">
                  {_$('确认')}
                </Button>
                <Button
                  onClick={() => {
                    history.goBack()
                  }}
                  type="default"
                >
                  {_$('取消')}
                </Button>
              </div>
            </>
          )}
        </Card>
      </CustomPageContainer>
    </div>
  )
}

export default styled(EditPage)`
  position: relative;

  .k-card {
    .k-card-body {
      height: calc(100vh - 228px);
      overflow-y: auto;
      position: relative;
    }
    .edit_title {
      padding: 16px 0;
      margin-top: 16px;
      position: relative;
      font-size: 16px;
      vertical-align: middle;
      font-weight: 700;
      border-top: 1px solid #f0f0f0;
      &:first-child {
        margin-top: 0;
        padding-top: 0;
        border: none;
      }
    }
    .duty_shift_item {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      & > * {
        margin-right: 8px;
      }
      & > .k-icon {
        cursor: pointer;
        transition: all 0.3s;
        &:hover {
          color: ${variable.primaryColor};
        }
      }
    }
  }

  .bottom_action {
    display: flex;
    justify-content: flex-end;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 64px;
    padding: 16px 0;
    background: #ffffff;
    padding-right: 16px;
    z-index: 98;
    border-radius: 6px 6px 0 0;
    button {
      font-size: 14px;
      margin-left: 8px;
    }
  }
`
